import React from 'react';

import { PopupContainer } from 'src/app/components/Popup/PopupContainer';
import { PopupError } from 'src/app/components/Popup/PopupError';
import { PopupJoinChat } from 'src/app/components/Popup/PopupJoinChat';
import { PopupPlaylist } from 'src/app/components/Popup/PopupPlaylist';
import { POPUPERROR_TYPE, POPUPERROR_TITLE } from 'src/app/constants/popup';
import { EventTrack } from 'src/app/helpers/analytics';
import { setBodyModal } from 'src/app/helpers/dom';
import { withLoginPopup } from 'src/app/helpers/login';
import { withToast } from 'src/app/helpers/toast';
import { Playlist } from 'src/app/models/playlist';
import { InjectedPopupPlaylistProps, InjectedPopupErrorProps, InjectedPopupJoinChatProps } from 'src/app/models/popup';
import { InjectedToastProps } from 'src/app/models/toast';

export function withPopupPlaylist(): <C>(WrappedComponent: C) => C {
  function wrapperComponent<C>(WrappedComponent: C): C;
  function wrapperComponent<P extends InjectedPopupPlaylistProps & InjectedToastProps>(WrappedComponent: React.ComponentType<P>): React.ComponentClass<Pick<P, Exclude<keyof P, keyof InjectedPopupPlaylistProps & InjectedToastProps>>> {
    type MyProps = P;

    interface MyState {
      isShowPopup: boolean;
      videoId: string;
      isInWatchlist: boolean;
      inPlaylists: Playlist[];
      userPlaylists: Playlist[];
      pushEventTrack?: EventTrack;
    }

    @withLoginPopup()
    @withToast()
    class MyClassWithPopupPlaylist extends React.Component<MyProps, MyState> {
      constructor(props: MyProps) {
        super(props);

        this.state = {
          isShowPopup: false,
          videoId: '',
          isInWatchlist: false,
          inPlaylists: [],
          userPlaylists: []
        };

        this.showPopup = this.showPopup.bind(this);
        this.closePopup = this.closePopup.bind(this);
      }

      componentWillUnmount(): void {
        setBodyModal(false);
      }

      render(): JSX.Element {
        return (
          <>
            {this.renderPopupContainer()}
            <WrappedComponent {...this.props} showPopupPlaylist={this.showPopup} />
          </>
        );
      }

      private renderPopupContainer(): JSX.Element | null {
        if (this.state.isShowPopup) {
          return (
            <div className="positon-absolute">
              <PopupContainer onClick={this.closePopup}>
                <PopupPlaylist
                  close={() => { this.closePopup(); }}
                  {...this.state}
                  showToast={this.props.showToast}
                />
              </PopupContainer>
            </div>
          );
        } else {
          return null;
        }
      }

      private showPopup(videoId: string, isInWatchlist: boolean, inPlaylists: Playlist[], userPlaylists: Playlist[], pushEventTrack?: EventTrack): void {
        setBodyModal(true);
        this.setState({
          isShowPopup: true,
          videoId,
          isInWatchlist,
          inPlaylists,
          userPlaylists,
          pushEventTrack
        });
      }

      private closePopup(): void {
        setBodyModal(false);
        this.setState({ isShowPopup: false });
      }
    }

    return MyClassWithPopupPlaylist;
  }

  return wrapperComponent;
}

export function withPopupError(): <C>(WrappedComponent: C) => C {
  function wrapperComponent<C>(WrappedComponent: C): C;
  function wrapperComponent<P extends InjectedPopupErrorProps >(WrappedComponent: React.ComponentType<P>): React.ComponentClass<Pick<P, Exclude<keyof P, keyof InjectedPopupErrorProps>>> {
    type MyProps = P;

    interface MyState {
      isShowPopup: boolean;
      errorType: POPUPERROR_TYPE;
      title?: POPUPERROR_TITLE;
      onClosePopupFunc?: VoidFunction;
    }

    class MyClassWithPopupError extends React.Component<MyProps, MyState> {
      constructor(props: MyProps) {
        super(props);

        this.state = {
          isShowPopup: false,
          errorType: POPUPERROR_TYPE.GENERAL
        };

        this.showPopup = this.showPopup.bind(this);
        this.closePopup = this.closePopup.bind(this);
      }

      componentWillUnmount(): void {
        setBodyModal(false);
      }

      render(): JSX.Element {
        return (
          <>
            {this.renderPopupContainer()}
            <WrappedComponent {...this.props} showPopupError={this.showPopup} />
          </>
        );
      }

      private renderPopupContainer(): JSX.Element | null {
        if (this.state.isShowPopup) {
          if (document.fullscreenElement) {
            document.exitFullscreen();
          }
          return (
            <PopupContainer>
              <PopupError
                close={this.closePopup}
                error={this.state.errorType}
                title={this.state.title}
              />
          </PopupContainer>
          );
        } else {
          return null;
        }
      }

      private showPopup(errorType: POPUPERROR_TYPE, title?: POPUPERROR_TITLE, onClosePopupFunc?: VoidFunction): void {
        setBodyModal(true);
        this.setState({
          isShowPopup: true,
          errorType,
          title,
          onClosePopupFunc
        });
      }

      private closePopup(): void {
        setBodyModal(false);
        this.setState({ isShowPopup: false });
        this.state.onClosePopupFunc && this.state.onClosePopupFunc();
      }
    }

    return MyClassWithPopupError;
  }

  return wrapperComponent;
}

export function withPopupJoinChat(): <C>(WrappedComponent: C) => C {
  function wrapperComponent<C>(WrappedComponent: C): C;
  function wrapperComponent<P extends InjectedPopupJoinChatProps >(WrappedComponent: React.ComponentType<P>): React.ComponentClass<Pick<P, Exclude<keyof P, keyof InjectedPopupJoinChatProps>>> {
    type MyProps = P;

    interface MyState {
      isShowPopup: boolean;
      onLoginKaskus: VoidFunction;
      onJoinGuestChat: VoidFunction;
      isFullscreen?: boolean;
      isDarkMode?: boolean;
    }

    class MyClassWithPopupJoinChat extends React.Component<MyProps, MyState> {
      constructor(props: MyProps) {
        super(props);

        this.state = {
          isShowPopup: false,
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          onLoginKaskus: () => {},
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          onJoinGuestChat: () => {}
        };

        this.showPopup = this.showPopup.bind(this);
        this.closePopup = this.closePopup.bind(this);
      }

      componentWillUnmount(): void {
        setBodyModal(false);
      }

      render(): JSX.Element {
        return (
          <>
            {this.renderJoinChatPopup()}
            <WrappedComponent {...this.props} showPopupJoinChat={this.showPopup} />
          </>
        );
      }

      private renderJoinChatPopup(): JSX.Element | null {
        if (this.state.isShowPopup) {
          if (document.fullscreenElement) {
            document.exitFullscreen();
          }
          return (
            <PopupContainer
              onClick={this.closePopup}
              isFullScreen={this.state.isFullscreen}
            >
              <PopupJoinChat
                onClickLoginGuest={() => { this.state.onJoinGuestChat(); this.closePopup(); }}
                onClickLoginKaskus={() => { this.state.onLoginKaskus(); this.closePopup(); }}
                isDarkMode={this.state.isDarkMode}
              />
            </PopupContainer>
          );
        } else {
          return null;
        }
      }

      private showPopup(onLoginKaskus: VoidFunction, onJoinGuestChat: VoidFunction, isFullscreen: false, isDarkMode: false): void {
        setBodyModal(true);
        this.setState({
          isShowPopup: true,
          onLoginKaskus,
          onJoinGuestChat,
          isFullscreen,
          isDarkMode
        });
      }

      private closePopup(): void {
        setBodyModal(false);
        this.setState({ isShowPopup: false });
      }
    }

    return MyClassWithPopupJoinChat;
  }

  return wrapperComponent;
}
